import { motion } from "framer-motion";
import { Award, BookOpen, Star, Music } from "lucide-react";

const VARSHA_IMAGE = "https://customer-assets.emergentagent.com/job_nataraja-dance/artifacts/im2sutzh_0f3fea33-2d0c-4181-9fb8-40ebaea08a24.jpeg";

const achievements = [
  {
    icon: Award,
    title: "Vidwat Examination",
    description: "Appeared for Vidwat Final in 2024 (Karnataka State Dr. Gangubai Hangal Music & Performing Arts University)"
  },
  {
    icon: BookOpen,
    title: "Pre-Vidwat (2020)",
    description: "Completed Pre-Vidwat examination conducted by KSEEB"
  },
  {
    icon: Star,
    title: "Senior Exam (2014)",
    description: "Excelled in Senior Bharatanatyam examination"
  },
  {
    icon: Music,
    title: "20+ Years Training",
    description: "Training since age 3.5 under renowned Gurus"
  }
];

export const GuruSection = () => {
  return (
    <section
      id="guru"
      className="section-darker py-24 md:py-32"
      data-testid="guru-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-gold/80 font-manrope text-sm tracking-[0.3em] uppercase mb-4">
            Meet Your Instructor
          </p>
          <h2 className="font-cinzel text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Vidhushi <span className="text-gold">Varsha R</span>
          </h2>
          <div className="section-divider mx-auto" />
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="guru-image-frame relative">
              <img
                src={VARSHA_IMAGE}
                alt="Vidhushi Varsha R - Bharatanatyam Instructor"
                className="w-full max-w-lg mx-auto gold-frame"
                data-testid="guru-image"
              />
            </div>
          </motion.div>

          {/* Right - Bio */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="gold-border-left pl-6">
              <p className="font-manrope text-white/80 text-lg leading-relaxed">
                Varsha R is an enthusiastic and committed individual whose journey 
                in Bharatanatyam began when she was just <span className="text-gold">three and a half years old</span>. 
                The elder daughter of Sri N. Ramu and Smt. Latha R, she grew up alongside 
                her loving younger sister, Vrithi R.
              </p>
            </div>

            <p className="font-manrope text-white/60 text-base leading-relaxed">
              Her foray into this divine art form started under the guidance of 
              <span className="text-gold"> Vidushi Nagaveni Guru</span> at Lalitha Nritya Kala Mandira. 
              Seeking to advance her skills further, she found her Gurus, 
              <span className="text-gold"> Karnataka Kalashree Guru K. Brunda</span> and 
              <span className="text-gold"> Vidushi Ananya M</span>, at Ananya Kala Niketana(R).
            </p>

            <p className="font-manrope text-white/60 text-base leading-relaxed">
              Over the past eight years, their unwavering support and mentorship have helped 
              her evolve as a dancer, deepening her understanding of this sacred art form 
              and refining her artistry and performance abilities.
            </p>

            <p className="font-manrope text-white/60 text-base leading-relaxed">
              Beyond dance, Varsha completed her <span className="text-gold">BCA</span> with specialization in 
              Mobile Application and Cloud Technology, followed by an <span className="text-gold">MBA</span> in 
              Human Resources and Marketing. She also worked as an Administrative Contractor 
              at IBM for two years, showcasing her remarkable versatility.
            </p>

            <p className="font-manrope text-white/60 text-base leading-relaxed">
              A true all-rounder, Varsha has won <span className="text-gold">district-level championships</span> in 
              badminton and javelin throw, secured first place in science talent exams, 
              and won the prestigious School All-Rounder Award.
            </p>
          </motion.div>
        </div>

        {/* Achievements Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {achievements.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="card-hover bg-[#1A1A1A] border border-white/5 p-6 hover:border-gold/30"
              data-testid={`achievement-${index}`}
            >
              <item.icon className="w-8 h-8 text-gold mb-4" />
              <h4 className="font-cinzel text-lg text-white mb-2">{item.title}</h4>
              <p className="font-manrope text-white/50 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="font-playfair text-xl md:text-2xl text-white/70 italic max-w-3xl mx-auto">
            "Today, she stands as a symbol of dedication, talent, and hard work, 
            with her diverse interests and her steadfast commitment to the art of Bharatanatyam."
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default GuruSection;
